import * as t from "io-ts";
import { cargoInvoiceStatusT, cargoQualityT, cargoTypeT, trailerQualityT, } from "./types";
import optional, { optionalOrEmpty } from "./optional";
import nullable from "./nullable";
import { placeT } from "./place";
import { CargoExtraCo2BaseT } from "./cargo-extra-co2";
import { unitT } from "./cargo-unit";
import { withFallback } from "io-ts-types";
import { createStopUnitTypes } from "./stop-unit";
import { addCmrConfigT, cmrConfigT } from "./cmr-config";
import { attachEquipmentToCargoT } from "./cargo-equipment";
const cargoPiecesT = t.array(t.strict({
    quantity: t.number,
    unit: unitT,
}));
export const createCargoTypes = ({ decimal, date, dateTime, }) => {
    const { stopUnitT, addStopUnitT } = createStopUnitTypes({ decimal });
    const stopT = t.type({
        loadmeters: decimal,
        height: optionalOrEmpty(decimal),
        width: optionalOrEmpty(decimal),
        length: optionalOrEmpty(decimal),
        weight: optionalOrEmpty(decimal),
        colli: optionalOrEmpty(decimal),
        cubicMeters: optionalOrEmpty(decimal),
        temperature: optional(t.string),
        place: nullable(placeT),
        ref: optional(t.string),
        tag: optional(t.string),
        contact: optional(t.string),
        contactPhone: optional(t.string),
        date: date,
        note: withFallback(nullable(t.string), null),
        openingDate: nullable(date),
        time: optional(t.string),
        openingTime: nullable(t.string),
        fix: t.boolean,
        pieces: cargoPiecesT,
    });
    const baseCargoWithoutRefT = t.strict({
        clientId: optional(t.string),
        customerContactId: optional(t.string),
        invoiceRef: optional(t.string),
        type: cargoTypeT,
        qualities: optional(t.array(cargoQualityT)),
        requirements: optional(t.array(trailerQualityT)),
        products: t.array(t.string),
        description: optional(t.string),
        description2: withFallback(optional(t.string), ""),
        clientContact: optional(t.string),
        clientContactPhone: optional(t.string),
        clientContactEmail: optional(t.string),
        driverCanAddDropoffs: t.boolean,
        driverCanRegisterTime: t.boolean,
        driverCanMoveFromToUndispatched: withFallback(t.boolean, false),
        cargoTagId: nullable(t.string),
        smartMatch: t.boolean,
        note: optional(t.string),
        assignedTeamId: t.string,
        containerNo: nullable(t.string),
        minTemperature: nullable(decimal),
        maxTemperature: nullable(decimal),
        adr: t.boolean,
        adrText: t.string,
        hideForDrivers: t.boolean,
        invoiceStatus: withFallback(cargoInvoiceStatusT, "OPEN"),
    });
    const baseCargoT = t.intersection([
        baseCargoWithoutRefT,
        t.strict({ ref: t.string }),
    ]);
    const baseCargoWithCreatedAtT = t.intersection([
        baseCargoT,
        t.strict({ createdAt: nullable(dateTime), userId: nullable(t.string) }),
    ]);
    const idT = t.strict({ id: t.string });
    const optionalIdT = t.partial({ id: t.string });
    const optionalProvisoryIdT = t.partial({ provisoryId: t.string });
    const editStopOutputT = t.intersection([stopT, idT]);
    const editStopInputT = t.intersection([
        stopT,
        optionalIdT,
        optionalProvisoryIdT,
    ]);
    const postCargoT = t.intersection([
        baseCargoWithoutRefT,
        t.strict({
            route_id: nullable(t.string),
            pickupList: t.array(editStopInputT),
            dropoffList: t.array(editStopInputT),
            stopUnits: withFallback(t.array(addStopUnitT), []),
            cmrConfigs: withFallback(t.array(addCmrConfigT), []),
            extraCo2Elements: t.array(CargoExtraCo2BaseT),
            clientCargoTemplateId: withFallback(nullable(t.string), null),
        }),
    ]);
    const createCargoInvoiceLineT = t.strict({
        productId: nullable(t.string),
        unitId: nullable(t.string),
        description: nullable(t.string),
        quantity: decimal,
        amount: decimal,
        discountPercentage: nullable(decimal),
        percentage: nullable(decimal),
        isFromRoadToll: t.boolean,
    });
    const postCargoWithInvoiceLinesT = t.intersection([
        postCargoT,
        t.strict({ invoiceLines: t.array(createCargoInvoiceLineT) }),
    ]);
    const equipmentOnCreateT = t.strict({
        equipment: withFallback(t.array(attachEquipmentToCargoT), []),
    });
    const postCargoWithEquipmentT = t.intersection([
        postCargoT,
        equipmentOnCreateT,
    ]);
    const postCargoWithInvoiceLinesAndEquipmentT = t.intersection([
        postCargoWithInvoiceLinesT,
        equipmentOnCreateT,
    ]);
    const createCargoOnDriverRoutesReqT = t.intersection([
        postCargoWithInvoiceLinesT,
        t.strict({
            driverIds: t.array(t.string),
            date: t.string,
        }),
    ]);
    const editStopUnitOutputT = t.intersection([stopUnitT, idT]);
    const editStopUnitInputT = t.intersection([addStopUnitT, optionalIdT]);
    const editCmrConfigInputT = t.intersection([addCmrConfigT, optionalIdT]);
    const editCargoOutputT = t.intersection([
        baseCargoWithCreatedAtT,
        idT,
        t.strict({
            canBeDeleted: t.boolean,
            invoiceStatusLocked: withFallback(t.boolean, false),
            deliveryId: t.string,
            pickupList: t.array(editStopOutputT),
            dropoffList: t.array(editStopOutputT),
            stopUnits: t.array(editStopUnitOutputT),
            cmrConfigs: withFallback(t.array(cmrConfigT), []),
            totalLm: decimal,
            totalWeight: nullable(decimal),
            revision: t.string,
            extraCo2Elements: t.array(CargoExtraCo2BaseT),
            products: t.array(t.string),
            hasBeenSplit: t.boolean,
            originalRouteIdBeforeSplit: nullable(t.string),
            splitFromCargoId: nullable(t.string),
            operationInProgress: withFallback(t.boolean, false),
            clientCargoTemplateId: nullable(t.string),
            distanceKm: withFallback(nullable(decimal), null),
        }),
    ]);
    const editCargoInputT = t.intersection([
        baseCargoT,
        t.strict({
            pickupList: t.array(editStopInputT),
            dropoffList: t.array(editStopInputT),
            stopUnits: withFallback(t.array(editStopUnitInputT), []),
            cmrConfigs: withFallback(t.array(editCmrConfigInputT), []),
            extraCo2Elements: t.array(CargoExtraCo2BaseT),
            products: t.array(t.string),
        }),
    ]);
    const editCargoOutputMultipleT = t.array(editCargoOutputT);
    return {
        editCargoInputT,
        editCargoOutputT,
        editCargoOutputMultipleT,
        postCargoT,
        createCargoInvoiceLineT,
        postCargoWithInvoiceLinesT,
        postCargoWithEquipmentT,
        postCargoWithInvoiceLinesAndEquipmentT,
        createCargoOnDriverRoutesReqT,
        baseCargoWithoutRefT,
        stopT,
        editStopInputT,
        editStopOutputT,
        stopUnitT,
        addStopUnitT,
        editStopUnitInputT,
        editStopUnitOutputT,
        cmrConfigT,
        addCmrConfigT,
        editCmrConfigInputT,
    };
};
export const moveToRouteResponseT = t.strict({
    updatedCargoOrders: t.array(t.strict({ routeId: t.string, cargoOrder: t.array(t.string) })),
});
export const copyCargoToDriverRoutesReqT = t.strict({
    driverIds: t.array(t.string),
    date: t.string,
    copyInvoiceLines: t.boolean,
    resetInvoiceLineQuantities: t.boolean,
});
export const copyCargoToDriverRoutesResultT = t.strict({
    driverId: t.string,
    routeId: t.string,
    cargoId: t.string,
    createdNewRoute: t.boolean,
});
export const copyCargoToDriverRoutesStartResT = t.strict({
    jobId: t.string,
});
export const copyCargoToDriverRoutesJobStatusT = t.union([
    t.literal("PENDING"),
    t.literal("IN_PROGRESS"),
    t.literal("COMPLETED"),
    t.literal("FAILED"),
]);
export const copyCargoToDriverRoutesJobT = t.strict({
    jobId: t.string,
    status: copyCargoToDriverRoutesJobStatusT,
    driverIndex: t.number,
    total: t.number,
    results: t.array(copyCargoToDriverRoutesResultT),
    error: nullable(t.string),
});
export const cargoSharedToStatusT = t.strict({
    contractId: nullable(t.string),
    status: t.union([
        t.literal("PENDING"),
        t.literal("ACCEPTED"),
        t.literal("REJECTED"),
        t.literal("NONE"),
    ]),
    subcontractorOrgName: nullable(t.string),
});
export const cargoSharedFromStatusT = t.strict({
    shared: t.boolean,
    contractId: nullable(t.string),
    contractorOrgName: nullable(t.string),
});
export const ERR_CARGO_ALREADY_DELETED = "ERR_CARGO_ALREADY_DELETED";
export const cargoAlreadyDeletedErrorT = t.intersection([
    t.strict({
        code: t.literal(ERR_CARGO_ALREADY_DELETED),
        message: t.string,
    }),
    t.exact(t.partial({
        deletedBy: nullable(t.string),
        deletedByDriver: nullable(t.string),
    })),
]);
export const cargoSplitInfoT = t.strict({
    cargoId: t.string,
    ref: nullable(t.string),
    clientId: nullable(t.string),
    deleted: t.boolean,
});
export const cargoSplitsInfoT = t.array(cargoSplitInfoT);
//# sourceMappingURL=cargo.js.map